import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuthHeader, useAuthUser } from 'react-auth-kit';
import { Modal, Container, Row, Col, ButtonToolbar, Button, Form, Card, Badge, Alert, Accordion } from 'react-bootstrap';
import * as Icon from 'react-feather';

function DefaultModal(props) {
    const apiUrl = process.env.REACT_APP_URL;
    const authHeader = useAuthHeader();
    const authUser = useAuthUser();
    const user = authUser();

    const [requestData, setRequestData] = useState(props.requestInfo || {});
    const [loading, setLoading] = useState(false);
    const [operators, setOperators] = useState([]);
    const [units, setUnits] = useState([]);
    const [rejectionReason, setRejectionReason] = useState('');
    const [showRejectForm, setShowRejectForm] = useState(false);
    const [stops, setStops] = useState([]);

    // Trip data for approval
    const [tripData, setTripData] = useState({
        operator_id: '',
        inventory_id: '',
        operations_id: '',
        monitor_id: '',
        invoice_id: '',
        cfditraslado: ''
    });

    const isClienteUser = props.isClienteUser;
    const isPending = requestData.status === 'pending';

    useEffect(() => {
        if (props.show) {
            setRequestData(props.requestInfo || {});
            setStops(props.requestInfo?.stops || []);
            if (!isClienteUser && isPending) {
                loadUsers();
                loadUnits();
            }
            // Load stops for this trip request
            if (props.requestInfo?.id) {
                loadTripRequestStops(props.requestInfo.id);
            }
        }
    }, [props.show, props.requestInfo]);

    const loadTripRequestStops = async (tripRequestId) => {
        try {
            const response = await axios.get(`${apiUrl}/trips/get_trip_request_stops?trip_request_id=${tripRequestId}`, {
                headers: {
                    Authorization: authHeader()
                }
            });
            setStops(response.data.msg.Stops || []);
        } catch (error) {
            console.error('Error loading trip request stops:', error);
            // Silently fail - stops may not exist
        }
    };

    const loadUsers = async () => {
        try {
            const response = await axios.get(`${apiUrl}/users/get_all_users?user_type=all`, {
                headers: {
                    Authorization: authHeader()
                }
            });
            const users = response.data.msg.Users || [];
            const operatorUsers = users.filter(user =>
                user.roles && user.roles.some(role => role.title.toLowerCase() === 'operador')
            );
            setOperators(operatorUsers);
        } catch (error) {
            console.error('Error loading users:', error);
        }
    };

    const loadUnits = async () => {
        try {
            const response = await axios.get(`${apiUrl}/units/get_all_units`, {
                headers: {
                    Authorization: authHeader()
                }
            });
            setUnits(response.data.msg.Units || []);
        } catch (error) {
            console.error('Error loading units:', error);
        }
    };

    const handleTripDataChange = (field, value) => {
        setTripData(prev => ({
            ...prev,
            [field]: value
        }));
    };

    const handleApprove = async () => {
        if (!tripData.operator_id || !tripData.inventory_id) {
            alert('Por favor selecciona un operador y una unidad');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                approved_by_user_id: user?.id,
                trip_data: {
                    operator_id: parseInt(tripData.operator_id),
                    inventory_id: parseInt(tripData.inventory_id),
                    operations_id: tripData.operations_id ? parseInt(tripData.operations_id) : null,
                    monitor_id: tripData.monitor_id ? parseInt(tripData.monitor_id) : null,
                    invoice_id: tripData.invoice_id,
                    cfditraslado: tripData.cfditraslado
                }
            };

            await axios.post(`${apiUrl}/trips/approve_trip_request/${requestData.id}`, payload, {
                headers: {
                    Authorization: authHeader()
                }
            });

            props.setData();
            props.onHide();
            alert('Solicitud aprobada y viaje creado exitosamente');
        } catch (error) {
            console.error('Error approving trip request:', error);
            alert('Error al aprobar la solicitud');
        } finally {
            setLoading(false);
        }
    };

    const handleReject = async () => {
        if (!rejectionReason.trim()) {
            alert('Por favor ingresa una razón para el rechazo');
            return;
        }

        setLoading(true);
        try {
            const payload = {
                rejected_by_user_id: user?.id,
                rejection_reason: rejectionReason
            };

            await axios.post(`${apiUrl}/trips/reject_trip_request/${requestData.id}`, payload, {
                headers: {
                    Authorization: authHeader()
                }
            });

            props.setData();
            props.onHide();
            alert('Solicitud rechazada');
        } catch (error) {
            console.error('Error rejecting trip request:', error);
            alert('Error al rechazar la solicitud');
        } finally {
            setLoading(false);
        }
    };

    const getStatusBadge = (status) => {
        switch (status?.toLowerCase()) {
            case 'approved':
                return <Badge bg="success" className="fs-6">Aprobado</Badge>;
            case 'pending':
                return <Badge bg="warning" className="fs-6">Pendiente</Badge>;
            case 'rejected':
                return <Badge bg="danger" className="fs-6">Rechazado</Badge>;
            default:
                return <Badge bg="secondary" className="fs-6">{status || 'N/A'}</Badge>;
        }
    };

    return (
        <Modal {...props} size="lg" aria-labelledby="contained-modal-title-vcenter">
            <Modal.Header closeButton>
                <Modal.Title id="contained-modal-title-vcenter">
                    <Icon.FileText size={20} className="me-2" />
                    Solicitud de Viaje #{requestData.id}
                </Modal.Title>
            </Modal.Header>

            <Modal.Body style={{ maxHeight: '70vh', overflowY: 'auto' }}>
                <Container>
                    {/* Status Card */}
                    <Card className="mb-3">
                        <Card.Header className="d-flex justify-content-between align-items-center">
                            <h6 className="mb-0">Estado de la Solicitud</h6>
                            {getStatusBadge(requestData.status)}
                        </Card.Header>
                        <Card.Body>
                            {requestData.status === 'approved' && (
                                <Alert variant="success">
                                    <Icon.CheckCircle size={16} className="me-2" />
                                    Aprobado por: {requestData.approved_by_name} el {requestData.approved_at}
                                    {requestData.trip_id && (
                                        <span className="ms-2">(Viaje ID: {requestData.trip_id})</span>
                                    )}
                                </Alert>
                            )}
                            {requestData.status === 'rejected' && (
                                <Alert variant="danger">
                                    <Icon.XCircle size={16} className="me-2" />
                                    Rechazado por: {requestData.approved_by_name} el {requestData.approved_at}
                                    {requestData.rejection_reason && (
                                        <div className="mt-2">
                                            <strong>Razón:</strong> {requestData.rejection_reason}
                                        </div>
                                    )}
                                </Alert>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Request Details */}
                    <Card className="mb-3">
                        <Card.Header>
                            <h6 className="mb-0">Detalles de la Solicitud</h6>
                        </Card.Header>
                        <Card.Body>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <p><strong>Cliente:</strong> {requestData.client_name}</p>
                                    <p><strong>Ruta:</strong> {requestData.route_name || 'N/A'}</p>
                                    <p><strong>Tipo de Carga:</strong> {requestData.details || 'N/A'}</p>
                                </Col>
                                <Col md={6}>
                                    <p><strong>Solicitado Por:</strong> {requestData.requested_by_name}</p>
                                    <p><strong>Fecha Creación:</strong> {requestData.created}</p>
                                    <p><strong>Última Actualización:</strong> {requestData.updated || 'N/A'}</p>
                                </Col>
                            </Row>
                            <Row className="mb-3">
                                <Col md={6}>
                                    <Card className="bg-light">
                                        <Card.Body className="text-center">
                                            <h6 className="text-primary">
                                                <Icon.Calendar size={16} className="me-1" />
                                                Inicio Solicitado
                                            </h6>
                                            <p className="mb-0 fs-5">{requestData.requested_start_date || 'N/A'}</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                                <Col md={6}>
                                    <Card className="bg-light">
                                        <Card.Body className="text-center">
                                            <h6 className="text-success">
                                                <Icon.Calendar size={16} className="me-1" />
                                                Fin Solicitado
                                            </h6>
                                            <p className="mb-0 fs-5">{requestData.requested_end_date || 'N/A'}</p>
                                        </Card.Body>
                                    </Card>
                                </Col>
                            </Row>
                            {requestData.notes && (
                                <Row>
                                    <Col>
                                        <p><strong>Notas:</strong></p>
                                        <p className="bg-light p-2 rounded">{requestData.notes}</p>
                                    </Col>
                                </Row>
                            )}
                        </Card.Body>
                    </Card>

                    {/* Trip Stops - Read-only view */}
                    {stops.length > 0 && (
                        <Card className="mb-3">
                            <Card.Header>
                                <h6 className="mb-0">
                                    <Icon.MapPin size={16} className="me-2" />
                                    Paradas del Viaje ({stops.length})
                                </h6>
                            </Card.Header>
                            <Card.Body>
                                <Accordion>
                                    {stops.map((stop, index) => (
                                        <Accordion.Item eventKey={index.toString()} key={index}>
                                            <Accordion.Header>
                                                <div className="d-flex justify-content-between w-100 me-3">
                                                    <span>
                                                        Parada {index + 1}: {stop.origin_address || 'Origen'} → {stop.destination_address || 'Destino'}
                                                    </span>
                                                </div>
                                            </Accordion.Header>
                                            <Accordion.Body>
                                                <Card className="border-0 bg-light">
                                                    <Card.Body className="py-3">
                                                        <Row className="align-items-center">
                                                            <Col md={4} className="text-center">
                                                                <div className="mb-2">
                                                                    <h6 className="mb-1 text-primary">Origen</h6>
                                                                    <div className="fw-bold">{stop.origin_date || 'N/A'}</div>
                                                                </div>
                                                                <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                                                                    {stop.origin_address || 'N/A'}
                                                                </div>
                                                            </Col>

                                                            <Col md={4} className="text-center">
                                                                <div className="d-flex align-items-center justify-content-center">
                                                                    <div className="border-top border-2 border-primary flex-grow-1"></div>
                                                                    <div className="mx-3">
                                                                        <Icon.Truck size={24} className="text-primary" />
                                                                    </div>
                                                                    <div className="border-top border-2 border-primary flex-grow-1"></div>
                                                                </div>
                                                            </Col>

                                                            <Col md={4} className="text-center">
                                                                <div className="mb-2">
                                                                    <h6 className="mb-1 text-success">Destino</h6>
                                                                    <div className="fw-bold">{stop.destination_date || 'N/A'}</div>
                                                                </div>
                                                                <div className="fw-semibold" style={{ fontSize: '0.9rem' }}>
                                                                    {stop.destination_address || 'N/A'}
                                                                </div>
                                                            </Col>
                                                        </Row>
                                                    </Card.Body>
                                                </Card>
                                                <Row className="mt-3">
                                                    <Col md={6}>
                                                        <p className="mb-1"><strong>Tipo de Carga:</strong> {stop.load_type || 'N/A'}</p>
                                                    </Col>
                                                    <Col md={6}>
                                                        <p className="mb-1"><strong>Peso:</strong> {stop.weight ? `${stop.weight} ton` : 'N/A'}</p>
                                                    </Col>
                                                </Row>
                                            </Accordion.Body>
                                        </Accordion.Item>
                                    ))}
                                </Accordion>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Approval Form - Only show for non-Cliente users and pending requests */}
                    {!isClienteUser && isPending && (
                        <Card className="mb-3">
                            <Card.Header className="bg-success text-white">
                                <h6 className="mb-0">
                                    <Icon.Check size={16} className="me-2" />
                                    Aprobar Solicitud
                                </h6>
                            </Card.Header>
                            <Card.Body>
                                <p className="text-muted small mb-3">
                                    Para aprobar esta solicitud, asigna un operador y una unidad para crear el viaje.
                                </p>
                                <Row className="mb-3">
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>Operador *</Form.Label>
                                        <Form.Select
                                            value={tripData.operator_id}
                                            onChange={(e) => handleTripDataChange('operator_id', e.target.value)}
                                            required
                                        >
                                            <option value="">Seleccionar operador...</option>
                                            {operators.map((operator) => (
                                                <option key={operator.id} value={operator.id}>
                                                    {operator.first_name} {operator.last_name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>Unidad *</Form.Label>
                                        <Form.Select
                                            value={tripData.inventory_id}
                                            onChange={(e) => handleTripDataChange('inventory_id', e.target.value)}
                                            required
                                        >
                                            <option value="">Seleccionar unidad...</option>
                                            {units.map((unit) => (
                                                <option key={unit.id} value={unit.id}>
                                                    {unit.name}
                                                </option>
                                            ))}
                                        </Form.Select>
                                    </Form.Group>
                                </Row>
                                <Row className="mb-3">
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>No. Factura (opcional)</Form.Label>
                                        <Form.Control
                                            value={tripData.invoice_id}
                                            onChange={(e) => handleTripDataChange('invoice_id', e.target.value)}
                                            placeholder="Número de factura"
                                        />
                                    </Form.Group>
                                    <Form.Group as={Col} md={6}>
                                        <Form.Label>CFDI Traslado (opcional)</Form.Label>
                                        <Form.Control
                                            value={tripData.cfditraslado}
                                            onChange={(e) => handleTripDataChange('cfditraslado', e.target.value)}
                                            placeholder="CFDI Traslado"
                                        />
                                    </Form.Group>
                                </Row>
                                <div className="text-end">
                                    <Button
                                        variant="success"
                                        onClick={handleApprove}
                                        disabled={loading}
                                    >
                                        <Icon.Check size={16} className="me-1" />
                                        {loading ? 'Procesando...' : 'Aprobar y Crear Viaje'}
                                    </Button>
                                </div>
                            </Card.Body>
                        </Card>
                    )}

                    {/* Rejection Form - Only show for non-Cliente users and pending requests */}
                    {!isClienteUser && isPending && (
                        <Card className="mb-3">
                            <Card.Header className="bg-danger text-white d-flex justify-content-between align-items-center">
                                <h6 className="mb-0">
                                    <Icon.X size={16} className="me-2" />
                                    Rechazar Solicitud
                                </h6>
                                <Button
                                    variant="light"
                                    size="sm"
                                    onClick={() => setShowRejectForm(!showRejectForm)}
                                >
                                    {showRejectForm ? 'Ocultar' : 'Mostrar'}
                                </Button>
                            </Card.Header>
                            {showRejectForm && (
                                <Card.Body>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Razón del Rechazo *</Form.Label>
                                        <Form.Control
                                            as="textarea"
                                            rows={3}
                                            value={rejectionReason}
                                            onChange={(e) => setRejectionReason(e.target.value)}
                                            placeholder="Ingresa la razón por la cual se rechaza esta solicitud..."
                                            required
                                        />
                                    </Form.Group>
                                    <div className="text-end">
                                        <Button
                                            variant="danger"
                                            onClick={handleReject}
                                            disabled={loading}
                                        >
                                            <Icon.X size={16} className="me-1" />
                                            {loading ? 'Procesando...' : 'Rechazar Solicitud'}
                                        </Button>
                                    </div>
                                </Card.Body>
                            )}
                        </Card>
                    )}
                </Container>
            </Modal.Body>

            <Modal.Footer>
                <Button variant="secondary" onClick={props.onHide}>
                    Cerrar
                </Button>
            </Modal.Footer>
        </Modal>
    );
}

class ModalWithGrid extends React.Component {
    constructor(...args) {
        super(...args);
        this.state = { modalShow: false };
    }

    render() {
        let close_modal = () => {
            this.props.setData();
            this.setState({ modalShow: false });
        }

        return (
            <ButtonToolbar>
                <Button
                    size="sm"
                    variant="outline-primary"
                    onClick={() => this.setState({ modalShow: true })}
                >
                    <Icon.Eye size={14} />
                </Button>
                <DefaultModal
                    show={this.state.modalShow}
                    onHide={close_modal}
                    setData={this.props.setData}
                    requestInfo={this.props.requestInfo}
                    isClienteUser={this.props.isClienteUser}
                />
            </ButtonToolbar>
        );
    }
}

export default ModalWithGrid;
